import {
  type ChangeEvent,
  type ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Dialog,
  FormControl,
  Paper,
  type PaperProps,
  Stack,
  Typography,
} from '@mui/material';
import ModalTemplate from '../modal/modal-template';
import { BaseModalRef } from '@/app/helpers/types';
import { useAppSelector, useOpen } from '@/app/hooks';
import { UIFormLabel } from '../ui/ui-form-label';
import { FormTextInput } from '../inputs/form-text-input';
import UIAutocomplete from '../ui/ui-autocmplete';
import { useGetFactorsBranchesQuery } from '@/app/api';
import {
  useEditCompanyEmployeeMutation,
  useLazyGetDataByPinflQuery,
} from '@/entities/didox/api';
import { eimzo } from '@/entities/eimzo';
import { EmployeeRow, RoleValues } from '.';
import { useNotification } from '../contexts/NotificationContext';
import { getFetchEror } from '@/app/helpers';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@/app/assets/icons/add.svg?react';

interface EmployeeModalProps {
  employee: EmployeeRow | null;
  onSuccess?: () => void;
}

interface FormValues {
  pinfl: string;
  fullName: string;
  role: RoleValues | null;
  branch: number | null;
}

const DEFAULT_FORM_VALUES: FormValues = {
  pinfl: '',
  fullName: '',
  role: null,
  branch: null,
};

const PINFL_LENGTH = 14;

type BranchAutocompletePaperProps = PaperProps & {
  children?: ReactNode;
  addBranchLabel: string;
  onAddBranch: () => void;
};

const BranchAutocompletePaper = ({
  children,
  addBranchLabel,
  onAddBranch,
  ...paperProps
}: BranchAutocompletePaperProps) => {
  return (
    <Paper {...paperProps}>
      {children}
      <Box sx={{ px: 1.5, pb: 4 }}>
        <Stack
          direction='row'
          spacing={1}
          alignItems='center'
          sx={{
            border: '1px solid rgba(11, 19, 112, 0.4)',
            borderRadius: '8px',
            cursor: 'pointer',
            width: 'fit-content',
            mx: 'auto',
            px: 1,
            py: 0.5,
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: '#E6F1FF',
            },
          }}
          onMouseDown={(event) => event.preventDefault()}
          onClick={onAddBranch}
        >
          <Box component={AddIcon} />
          <Typography fontSize='inherit' color='#0B1370'>
            {addBranchLabel}
          </Typography>
        </Stack>
      </Box>
    </Paper>
  );
};

export const EmployeeModal = forwardRef<BaseModalRef, EmployeeModalProps>(
  ({ employee, onSuccess }, ref) => {
    const { t } = useTranslation();
    const { open, setOpen } = useOpen();
    const { profile } = useAppSelector((state) => state.auth);
    const { showNotification } = useNotification();
    const isEdit = Boolean(employee);
    const navigate = useNavigate();

    const { reset, control, handleSubmit, setValue, watch } =
      useForm<FormValues>({
        defaultValues: DEFAULT_FORM_VALUES,
      });

    const selectedBranch = watch('branch');

    const resetFormBasedOnMode = useCallback(() => {
      if (employee) {
        reset({
          fullName: employee?.name,
          pinfl: employee?.pinfl ?? '',
          role: employee?.role as RoleValues,
          branch: employee?.branch?.id ?? null,
        });
      } else {
        reset(DEFAULT_FORM_VALUES);
      }
    }, [employee, reset]);

    const onOpen = useCallback(() => {
      setOpen(true);
      if (employee) {
        resetFormBasedOnMode();
      }
    }, [employee, setOpen, resetFormBasedOnMode]);

    const onClose = useCallback(() => {
      setOpen(false);
      if (employee) {
        resetFormBasedOnMode();
      } else {
        reset(DEFAULT_FORM_VALUES);
      }
    }, [employee, reset, setOpen, resetFormBasedOnMode]);

    useImperativeHandle(
      ref,
      () => ({
        open: onOpen,
        close: onClose,
      }),
      [onOpen, onClose]
    );

    const ROLE_OPTIONS = useMemo(
      () => [
        { value: 19000 as RoleValues, label: t('roles.19000') },
        { value: 19001 as RoleValues, label: t('roles.19001') },
        { value: 19002 as RoleValues, label: t('roles.19002') },
      ],
      [t]
    );

    useEffect(() => {
      resetFormBasedOnMode();
    }, [resetFormBasedOnMode]);

    const { data: branches, isLoading: isBranchesLoading } =
      useGetFactorsBranchesQuery();

    const [editCompanyEmployee, { isLoading }] =
      useEditCompanyEmployeeMutation();

    const [getDataByPinfl, { isLoading: isDataByPinflLoading }] =
      useLazyGetDataByPinflQuery();

    const onSubmit = useCallback(
      async (data: FormValues) => {
        try {
          if (!profile?.tin) return;

          const basePayload = {
            tin: profile.tin,
            fio: data.fullName,
            fiztin: data.pinfl,
          };

          const gnkpermissions = await eimzo.sign(
            JSON.stringify({
              ...basePayload,
              roles: [],
            }),
            profile.tin
          );

          const internalpermissions = await eimzo.sign(
            JSON.stringify({
              ...basePayload,
              roles: data.role ? [String(data.role)] : [],
              branch: data.branch ?? null,
            }),
            profile.tin
          );

          await editCompanyEmployee({
            gnkpermissions,
            internalpermissions,
            is_director: 0,
          }).unwrap();

          reset(DEFAULT_FORM_VALUES);

          showNotification(
            isEdit
              ? t('settings.employee_successfully_updated')
              : t('settings.employee_successfully_added'),
            'success'
          );

          onSuccess?.();
          onClose();
        } catch (error) {
          showNotification(getFetchEror(error), 'error');
        }
      },
      [
        editCompanyEmployee,
        isEdit,
        onClose,
        onSuccess,
        profile?.tin,
        reset,
        showNotification,
        t,
      ]
    );

    const handleAddBranch = useCallback(() => {
      navigate('/factor/settings?activeTab=1');
    }, [navigate]);

    const renderBranchAutocompletePaper = useCallback(
      (props: PaperProps) => (
        <BranchAutocompletePaper
          {...props}
          addBranchLabel={t('settings.no_branches_button')}
          onAddBranch={handleAddBranch}
        />
      ),
      [handleAddBranch, t]
    );

    const handlePinflChange = useCallback(
      async (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (value.length !== PINFL_LENGTH) return;

        const response = await getDataByPinfl({ pinfl: value }).unwrap();
        setValue(
          'fullName',
          response.fullName ||
            response.fullname ||
            response.shortName ||
            response.shortname ||
            ''
        );
      },
      [getDataByPinfl, setValue]
    );

    const title = isEdit
      ? t('settings.employee_card')
      : t('settings.employees.add_employee');

    const submitText = isEdit ? t('save') : t('settings.add_condition');

    return (
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { maxWidth: 600, width: '100%' } }}
      >
        <ModalTemplate
          title={title}
          submitBtnText={submitText}
          cancelBtnText={t('settings.cancel')}
          onClose={onClose}
          onSubmit={handleSubmit(onSubmit)}
          loading={isLoading}
        >
          <Stack spacing={2}>
            <FormControl required>
              <UIFormLabel>{t('employee_pinfl')}:</UIFormLabel>
              <FormTextInput
                control={control}
                name='pinfl'
                required
                placeholder={t('common:payment_document.notfilled')}
                onChange={handlePinflChange}
              />
            </FormControl>

            <FormControl required>
              <UIFormLabel>
                {t('settings.employees.table_labels.full_name')}:
              </UIFormLabel>
              <FormTextInput
                control={control}
                name='fullName'
                required
                placeholder={t('common:payment_document.notfilled')}
                disabled={isDataByPinflLoading}
              />
            </FormControl>

            <FormControl required>
              <UIFormLabel>
                {t('settings.employees.table_labels.role')}:
              </UIFormLabel>

              <Controller
                control={control}
                name='role'
                rules={{ required: t('errors:required_field') }}
                render={({ field, fieldState }) => (
                  <UIAutocomplete
                    options={ROLE_OPTIONS}
                    value={
                      ROLE_OPTIONS.find((o) => o.value === field.value) ?? null
                    }
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) =>
                      option.value === value.value
                    }
                    onChange={(_, value) =>
                      field.onChange(value?.value ?? null)
                    }
                    onBlur={field.onBlur}
                    TextFieldProps={{
                      placeholder: t('common:not_selected'),
                      error: fieldState.invalid,
                      helperText: fieldState.error?.message,
                      required: true,
                    }}
                  />
                )}
              />
            </FormControl>

            <FormControl>
              <UIFormLabel>{t('branch')}:</UIFormLabel>

              <UIAutocomplete
                options={branches?.data ?? []}
                loading={isBranchesLoading}
                PaperComponent={renderBranchAutocompletePaper}
                value={
                  branches?.data?.find((b) => b.unique === selectedBranch) ??
                  null
                }
                getOptionLabel={(option) => option.branchName}
                onChange={(_, value) =>
                  setValue('branch', value?.unique ?? null)
                }
                TextFieldProps={{
                  placeholder: t('common:not_selected'),
                }}
              />
            </FormControl>
          </Stack>
        </ModalTemplate>
      </Dialog>
    );
  }
);
